import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import measurementService from '../../../services/measurementService';
import { getMeasurementErrorMessage } from '../../../services/measurementService';
import patientService from '../../../services/patientService';
import { normalizePeriodName } from '../../../utils/scheduleTargetUtils';
import { determinePeriodFromTime, getInstantPeriodOptions, PERMANENT_START_TIMES } from '../../../utils/measurementPeriodUtils';
import AddFormModal from '../../../components/AddFormModal/AddFormModal';
import './AddBPForm.css';

const AddBPForm = ({ isOpen, onClose, practiceId, patientId, onSuccess, modalClassName = '', initialScheduleTargets = null }) => {
  const [loading, setLoading] = useState(false);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [isAutoPeriod, setIsAutoPeriod] = useState(true);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    period: '',
    systolic: '',
    diastolic: '',
    pulse: '',
    note: ''
  });

  useEffect(() => {
    if (!isOpen || !practiceId) return;
    setIsAutoPeriod(true);
    setFormData({
      date: '',
      time: '',
      period: '',
      systolic: '',
      diastolic: '',
      pulse: '',
      note: ''
    });
    setErrors({});
    if (initialScheduleTargets && typeof initialScheduleTargets === 'object') {
      setScheduleTargets(initialScheduleTargets);
    } else {
      fetchScheduleTargets();
    }
  }, [isOpen, practiceId, patientId, initialScheduleTargets]);

  useEffect(() => {
    if (!isAutoPeriod) return;
    const determined = formData.time
      ? determinePeriodFromTime(
        formData.time,
        Array.isArray(scheduleTargets?.bloodPressureTimePeriod) ? scheduleTargets.bloodPressureTimePeriod : [],
        PERMANENT_START_TIMES.bloodPressure
      )
      : '';
    setFormData((prev) => (prev.period === (determined || '') ? prev : { ...prev, period: determined || '' }));
  }, [scheduleTargets, isAutoPeriod, formData.time]);

  const fetchScheduleTargets = async () => {
    try {
      const effectiveTargets = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
      setScheduleTargets(effectiveTargets);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const resolvedPeriod = isAutoPeriod
      ? (
        determinePeriodFromTime(
          formData.time,
          Array.isArray(scheduleTargets?.bloodPressureTimePeriod) ? scheduleTargets.bloodPressureTimePeriod : [],
          PERMANENT_START_TIMES.bloodPressure
        ) || ''
      )
      : formData.period;

    if (isAutoPeriod && resolvedPeriod !== formData.period) {
      setFormData((prev) => ({ ...prev, period: resolvedPeriod }));
    }

    const newErrors = {};
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    if (!resolvedPeriod) newErrors.period = 'Period is required';
    if (!formData.systolic) newErrors.systolic = 'Systolic is required';
    if (!formData.diastolic) newErrors.diastolic = 'Diastolic is required';
    if (!formData.pulse) newErrors.pulse = 'Pulse is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const selectedPeriod = uniquePeriods.find(
        (p) => normalizePeriodName(p.name) === normalizePeriodName(formData.period)
      );
      const periodId = selectedPeriod?.id ?? null;
      await measurementService.createBloodPressureMeasurement(practiceId, patientId, {
        measurement_date: formData.date,
        measurement_time: formData.time,
        period_id: periodId,
        period_name: resolvedPeriod,
        systolic_pressure: parseFloat(formData.systolic),
        diastolic_pressure: parseFloat(formData.diastolic),
        pulse: parseInt(formData.pulse, 10),
        note: formData.note || null
      });
      toast.success('Blood pressure measurement added successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(getMeasurementErrorMessage(error, 'Failed to add measurement'));
    } finally {
      setLoading(false);
    }
  };

  const uniquePeriods = useMemo(
    () => getInstantPeriodOptions(scheduleTargets?.bloodPressureTimePeriod || [], PERMANENT_START_TIMES.bloodPressure),
    [scheduleTargets]
  );

  return (
    <AddFormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Measurement Data"
      className={`abp-modal ${modalClassName}`.trim()}
      footer={(
        <div className="add-practice-form-actions abp-form-actions">
          <button type="button" className="add-practice-btn add-practice-btn-cancel" onClick={onClose} disabled={loading}>Close</button>
          <button type="submit" className="add-practice-btn add-practice-btn-add" disabled={loading}>{loading ? 'Saving...' : 'Add'}</button>
        </div>
      )}
    >
      <section className="abp-section">
        <h6 className="abp-section-title">Blood Pressure</h6>
        <div className="abp-grid">
          <div className="abp-field"><label>Date <span className="abp-req">*</span></label><input type="date" value={formData.date} onChange={(e) => setFormData((p) => ({ ...p, date: e.target.value }))} />{errors.date && <div className="abp-error">{errors.date}</div>}</div>
          <div className="abp-field"><label>Time <span className="abp-req">*</span></label><input type="time" value={formData.time} onChange={(e) => setFormData((p) => ({ ...p, time: e.target.value }))} />{errors.time && <div className="abp-error">{errors.time}</div>}</div>
          <div className="abp-field"><label>Period <span className="abp-req">*</span></label><select value={isAutoPeriod ? 'AUTO' : formData.period} onChange={(e) => { const v = e.target.value; if (v === 'AUTO') { setIsAutoPeriod(true); setFormData((p) => ({ ...p, period: determinePeriodFromTime(p.time, Array.isArray(scheduleTargets?.bloodPressureTimePeriod) ? scheduleTargets.bloodPressureTimePeriod : [], PERMANENT_START_TIMES.bloodPressure) || '' })); } else { setIsAutoPeriod(false); setFormData((p) => ({ ...p, period: v })); } }}><option value="AUTO">Automatically determine Measurement Period</option>{uniquePeriods.map((p) => <option key={p.id || p.name} value={p.name}>{p.name}</option>)}</select>{errors.period && <div className="abp-error">{errors.period}</div>}</div>
          <div className="abp-field"><label>Pulse <span className="abp-req">*</span></label><input type="number" value={formData.pulse} onChange={(e) => setFormData((p) => ({ ...p, pulse: e.target.value }))} />{errors.pulse && <div className="abp-error">{errors.pulse}</div>}</div>
          <div className="abp-field"><label>Systolic <span className="abp-req">*</span></label><input type="number" value={formData.systolic} onChange={(e) => setFormData((p) => ({ ...p, systolic: e.target.value }))} />{errors.systolic && <div className="abp-error">{errors.systolic}</div>}</div>
          <div className="abp-field"><label>Diastolic <span className="abp-req">*</span></label><input type="number" value={formData.diastolic} onChange={(e) => setFormData((p) => ({ ...p, diastolic: e.target.value }))} />{errors.diastolic && <div className="abp-error">{errors.diastolic}</div>}</div>
        </div>
        <div className="abp-field"><label>Note</label><textarea rows="2" value={formData.note} onChange={(e) => setFormData((p) => ({ ...p, note: e.target.value }))} /></div>
      </section>
    </AddFormModal>
  );
};

export default AddBPForm;
